import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactNode, ReactElement, Ref, RefObject } from 'react';
import { useRef } from 'react';
import styles from './GridListItem.module.scss';
import { setupTransitionInTimeline } from './GridListItem.transitions';
import type { GridListItemProps, GridListItemRef } from './GridListItem.data';
import { useScrollTransition } from 'util/react-transition-component';
import { NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer';
import Heading from '../../../atoms/heading/Heading';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';
import { useIsDesktopDevice } from '../../../../hooks/useIsDesktopDevice';

export default function GridListItem({
  transitionInDelay,
  blok,
  ref,
  ...props
}: GridListItemProps & { ref?: Ref<GridListItemRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLLIElement | null>;
  const lineRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HeadingRef>(null);
  const isDesktopDevice = useIsDesktopDevice(false);

  useScrollTransition({
    scroll: {
      transitionInDelay,
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        lineRef,
        headingRef,
      },
      setupTransitionInTimeline,
    }),
  });

  return (
    <li
      ref={elementRef}
      className={classNames(styles.gridListItem, props.className)}
      {...sbEditable(blok)}
    >
      <span ref={lineRef} className={classNames(styles.line)} />
      <Heading
        ref={headingRef}
        as="p"
        size="heading-05"
        className={classNames(styles.description)}
        transitionInType={isDesktopDevice ? 'splitLineRotateIn' : 'splitLineRotateIn'}
        disableTriggerInOnScroll
      >
        {render(blok.title, {
          nodeResolvers: {
            [NODE_PARAGRAPH]: (children: ReactNode) => <>{children}</>,
          },
        })}
      </Heading>
    </li>
  );
}
